import { ApiError, type ApiErrorResponse } from "./api";
import { log } from "./log";
import {
  isCancelledError,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import type { ReactNode } from "react";

export type CreateQueryClientOptions = {
  onQueryError?: (
    error: unknown,
    query: {
      queryKey: readonly unknown[];
      queryHash: string;
      state: unknown;
    },
  ) => void;
  onMutationError?: (
    error: unknown,
    context: {
      mutationKey?: readonly unknown[];
      variables: unknown;
      mutationId: number;
    },
  ) => void;
  onUnauthorized?: (error: unknown) => void | Promise<void>;
  getErrorMessage?: (error: unknown) => string;
};

/**
 * A request the app itself called off is not a failure: a component unmounted,
 * a file upload was removed mid-flight, a query was superseded. Nobody is
 * waiting for the answer and nobody can act on the "error".
 *
 * Two shapes reach us. React Query raises its own CancelledError when it
 * cancels a query. But an AbortController the app owns — the upload cancel
 * button, say — aborts the fetch directly, and that surfaces as the raw abort
 * error, which React Query has no reason to recognise as a cancellation.
 *
 * The name is matched rather than `instanceof DOMException` because React
 * Native's fetch rejects with a plain Error named AbortError instead.
 */
function isCancellation(error: unknown): boolean {
  if (isCancelledError(error)) {
    return true;
  }
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: unknown }).name === "AbortError"
  );
}

/**
 * Creates a QueryClient with optional error and unauthorized handlers.
 * App can pass log, toast, signOut, and getErrorMessage (e.g. i18n).
 */
function isSilentStatus(
  error: unknown,
  meta: Record<string, unknown> | undefined,
): boolean {
  const statuses = meta?.silentStatuses;
  if (!Array.isArray(statuses) || statuses.length === 0) {
    return false;
  }
  const status =
    error instanceof ApiError
      ? error.status
      : typeof error === "object" && error !== null && "status" in error
        ? (error as { status?: unknown }).status
        : undefined;
  return typeof status === "number" && statuses.includes(status);
}

export function createQueryClient(options: CreateQueryClientOptions = {}) {
  const { onQueryError, onMutationError, onUnauthorized } = options;

  return new QueryClient({
    queryCache: new QueryCache({
      onError: async (error, query) => {
        // Reported before anything else runs, so the guard has to come first:
        // log.error is wired to Sentry.captureException, and a cancelled
        // request would otherwise raise an issue for a user changing their mind.
        if (isCancellation(error)) {
          return;
        }

        const apiError = error as ApiErrorResponse;

        // A query may declare HTTP statuses that are an expected answer rather
        // than a failure (meta.silentStatuses, e.g. a 403 that just means
        // "not for you"): those are not reported, the caller renders nothing.
        if (isSilentStatus(error, query.meta)) {
          return;
        }

        log.error(error, {
          tags: {
            query_key: query.queryKey.join("/"),
            error_type: "query",
            error_code: apiError?.error?.code ?? "unknown",
          },
          extra: {
            apiError: apiError?.error,
            queryKey: query.queryKey,
            queryHash: query.queryHash,
            queryState: query.state,
          },
        });

        // Call the onQueryError handler if it is provided.
        if (onQueryError) {
          onQueryError(error, {
            queryKey: query.queryKey,
            queryHash: query.queryHash,
            state: query.state,
          });
        }

        // Call the onUnauthorized handler if the error is unauthorized.
        if (apiError?.error?.code === "UNAUTHORIZED" && onUnauthorized) {
          await onUnauthorized(error);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: async (error, variables, context, mutation) => {
        if (isCancellation(error)) {
          return;
        }

        const apiError = error as ApiErrorResponse;

        log.error(error, {
          tags: {
            error_type: "mutation",
            error_code: apiError?.error?.code ?? "unknown",
            mutation_key: mutation.options.mutationKey?.join("/") ?? "unknown",
          },
          extra: {
            context,
            apiError: apiError?.error,
            variables,
            mutationId: mutation.mutationId,
            mutationKey: mutation.options.mutationKey,
            mutationState: mutation.state,
          },
        });

        // Call the onMutationError handler if it is provided.
        if (onMutationError) {
          onMutationError(error, {
            mutationKey: mutation.options.mutationKey,
            variables,
            mutationId: mutation.mutationId,
          });
        }

        // Call the onUnauthorized handler if the error is unauthorized.
        if (apiError?.error?.code === "UNAUTHORIZED" && onUnauthorized) {
          await onUnauthorized(error);
        }
      },
    }),
    defaultOptions: {
      queries: {
        /**
         * Never retry a client error; retry everything else up to 3 times.
         *
         * A 4xx is the server's considered answer, not a blip: a 404 for a
         * module in another tenant, or a 403 for a feature this module is not
         * entitled to, returns exactly the same thing on every attempt. Retrying
         * turned each one into four requests, and a page that fires several
         * per-module queries then produced a burst of a dozen-plus doomed calls —
         * plus four rounds of error logging for a single user-visible failure.
         *
         * 408 and 429 are the exceptions: both are explicitly retryable, one
         * because the request timed out and one because the server asked us to
         * back off.
         */
        retry: (failureCount, error) => {
          // A cancelled request must never be retried. React Query short-
          // circuits its OWN cancellations before this predicate is consulted,
          // but an abort the app raised itself arrives here as an ordinary
          // error with no status — which the final branch would retry three
          // times, re-issuing a request the app deliberately called off.
          if (isCancellation(error)) {
            return false;
          }

          const status = error instanceof ApiError ? error.status : undefined;
          if (status && status >= 400 && status < 500) {
            return status === 408 || status === 429 ? failureCount < 3 : false;
          }
          return failureCount < 3;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
      },
    },
  });
}

/**
 * Provider that wraps children with QueryClientProvider.
 * App creates the client with createQueryClient() and passes it; app can add DevTools in its tree.
 */
export function TanstackQueryProvider({
  client,
  children,
}: {
  client: QueryClient;
  children: ReactNode;
}) {
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}

export type { QueryClient };
